import React, { useEffect } from "react";
import { Modal, Form, Input, Switch, Space, Select } from "antd";
import { BankOutlined } from "@ant-design/icons";
import { generateCodeFromName } from "../../../utils/codeUtils";

const { TextArea } = Input;
const { Option } = Select;

const DepartmentModal = ({
  visible,
  onCancel,
  onSubmit,
  department,
  loading,
  staffList = [],
  subjects = [],
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && department) {
      // Edit mode
      form.setFieldsValue({
        name: department.name,
        description: department.description || "",
        isActive: department.isActive,
        headOfDepartmentId: department.headOfDepartmentId || null,
        subjectIds: Array.isArray(department.subjects)
          ? department.subjects.map((s) => s.id)
          : [],
      });
    } else if (visible) {
      // Create mode
      form.resetFields();
      form.setFieldsValue({ isActive: true, subjectIds: [] });
    }
  }, [visible, department, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const normalizedName = values.name?.trim() || "";
      const shouldKeepExistingCode =
        department && normalizedName === (department.name || "").trim();
      const generatedCode = generateCodeFromName(normalizedName);
      const finalCode =
        shouldKeepExistingCode && department?.code
          ? department.code
          : generatedCode || department?.code || "";

      const payload = {
        ...values,
        code: finalCode,
        subjectIds: values.subjectIds || [],
      };

      const success = await onSubmit(payload);
      if (success) {
        form.resetFields();
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <BankOutlined />
          <span>{department ? "Edit Item" : "Create Item"}</span>
        </Space>
      }
      open={visible}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={500}
    >
      <Form form={form} layout="vertical" initialValues={{ isActive: true }}>
        <Form.Item
          name="name"
          label="Item Name"
          rules={[
            { required: true, message: "Please enter item name" },
            { max: 100, message: "Name cannot exceed 100 characters" },
          ]}
        >
          <Input placeholder="e.g., Finance, Administration, Academics" />
        </Form.Item>

        <Form.Item name="headOfDepartmentId" label="Item Owner">
          <Select
            placeholder="Select item owner"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) => {
              const label = String(option.children || "");
              return label.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {staffList.map((staff) => (
              <Option key={staff.userID} value={staff.userID}>
                {staff.fullName || staff.email}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} placeholder="Optional description of this item" />
        </Form.Item>

        <Form.Item name="subjectIds" label="Subjects">
          <Select
            mode="multiple"
            placeholder="Select subjects owned by this department (optional)"
            allowClear
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) => {
              const label = String(option.label || "");
              return label.toLowerCase().includes(input.toLowerCase());
            }}
            options={subjects.map((s) => ({
              value: s.id,
              label: s.name || s.subject,
            }))}
          />
        </Form.Item>

        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DepartmentModal;
